/**
 * What the game saves on the device, as a versioned envelope `{ version, data }`. New versions add fields through
 * migrations, one version at a time, so a player's pet and picture book survive an update. The browser wrapper that
 * reads and writes localStorage lives in apps/web; this module is pure (text in, typed result out).
 *
 * Player-typed text (the pet's name) is saved here, on the device, and nowhere else (docs/SPEC.md section 3).
 */
import { grandfather, type EarnedAchievement } from "./achievements";
import type { PetLog } from "./types";

export const CURRENT_VERSION = 2;

export interface Settings {
  controls: "touch" | "retro";
  meterStyle: "bars" | "numbers";
  meterLabels: "icons" | "names";
  sound: boolean;
  /** null means follow the device setting. */
  reduceMotion: boolean | null;
  largeText: boolean;
  notifications: boolean;
}

/** Which cosmetic is chosen in each slot. A value of -1 means none. */
export interface Looks {
  background: number;
  toy: number;
  companion: number;
  decoration: number;
  shellColour: number;
  shellPattern: number;
}

export interface BookEntry {
  weekId: string;
  species: string;
  name: string;
  points: number;
  looks: Looks;
}

export interface SaveGame {
  pet: PetLog | null;
  petName: string | null;
  lastHatchedWeekId: string | null;
  looks: Looks;
  book: BookEntry[];
  settings: Settings;
  /** The week of the first hatch: the 12 week drip window counts from here (docs/plans/010-achievements.md). */
  startedWeekId: string | null;
  achievements: EarnedAchievement[];
}

export type LoadError =
  | { kind: "corrupt" }
  | { kind: "invalid" }
  | { kind: "future-version"; version: number }
  | { kind: "missing-migration"; from: number };

export type LoadResult = { ok: true; data: SaveGame } | { ok: false; error: LoadError };

export interface Envelope {
  version: number;
  data: unknown;
}

/** A migration upgrades data from version N to N + 1; it is stored under the key N. */
export type Migrations = Record<number, (data: unknown) => unknown>;

/** 1 to 2: achievements. Whatever an old save already earned is recorded (with its rewards) without any announcement. */
const MIGRATIONS: Migrations = {
  1: (data) => {
    if (!isObject(data) || !Array.isArray(data.book) || !data.book.every((b) => isObject(b) && isString(b.weekId) && isNumber(b.points))) return data;
    try {
      return grandfather({ ...(data as unknown as SaveGame), startedWeekId: null, achievements: [] });
    } catch {
      return data;
    }
  },
};

export function emptySave(): SaveGame {
  return {
    pet: null,
    petName: null,
    lastHatchedWeekId: null,
    looks: { background: 0, toy: 0, companion: 0, decoration: 0, shellColour: 0, shellPattern: 0 },
    book: [],
    settings: {
      controls: "touch",
      meterStyle: "bars",
      meterLabels: "icons",
      sound: true,
      reduceMotion: null,
      largeText: false,
      notifications: false,
    },
    startedWeekId: null,
    achievements: [],
  };
}

export function serialize(data: SaveGame): string {
  return JSON.stringify({ version: CURRENT_VERSION, data });
}

/** Upgrades an envelope to the target version, or says why it cannot. */
export function migrateEnvelope(envelope: Envelope, target: number, migrations: Migrations): Envelope | LoadError {
  if (envelope.version > target) return { kind: "future-version", version: envelope.version };
  let version = envelope.version;
  let data = envelope.data;
  while (version < target) {
    const step = migrations[version];
    if (!step) return { kind: "missing-migration", from: version };
    data = step(data);
    version += 1;
  }
  return { version, data };
}

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null && !Array.isArray(x);
const isNumber = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);
const isString = (x: unknown): x is string => typeof x === "string";

function isPetLog(x: unknown): x is PetLog {
  return (
    isObject(x) &&
    isString(x.species) &&
    isNumber(x.hatchedAt) &&
    isString(x.weekId) &&
    isString(x.timeZone) &&
    isObject(x.curves) &&
    (x.refills === undefined || (isObject(x.refills) && Object.values(x.refills).every(isNumber))) &&
    Array.isArray(x.care) &&
    x.care.every((c) => isObject(c) && isNumber(c.at) && isString(c.meter) && (c.fill === undefined || isNumber(c.fill)) && (c.stop === undefined || typeof c.stop === "boolean")) &&
    Array.isArray(x.sessions) &&
    x.sessions.every((s) => isObject(s) && isNumber(s.openAt) && isNumber(s.lastSeenAt))
  );
}

function isLooks(x: unknown): x is Looks {
  return isObject(x) && ["background", "toy", "companion", "decoration", "shellColour", "shellPattern"].every((k) => isNumber(x[k]));
}

function isSettings(x: unknown): x is Settings {
  return (
    isObject(x) &&
    (x.controls === "touch" || x.controls === "retro") &&
    (x.meterStyle === "bars" || x.meterStyle === "numbers") &&
    (x.meterLabels === "icons" || x.meterLabels === "names") &&
    typeof x.sound === "boolean" &&
    (x.reduceMotion === null || typeof x.reduceMotion === "boolean") &&
    typeof x.largeText === "boolean" &&
    typeof x.notifications === "boolean"
  );
}

function isSaveGame(x: unknown): x is SaveGame {
  return (
    isObject(x) &&
    (x.pet === null || isPetLog(x.pet)) &&
    (x.petName === null || isString(x.petName)) &&
    (x.lastHatchedWeekId === null || isString(x.lastHatchedWeekId)) &&
    isLooks(x.looks) &&
    Array.isArray(x.book) &&
    x.book.every((b) => isObject(b) && isString(b.weekId) && isString(b.species) && isString(b.name) && isNumber(b.points) && isLooks(b.looks)) &&
    isSettings(x.settings) &&
    (x.startedWeekId === null || isString(x.startedWeekId)) &&
    Array.isArray(x.achievements) &&
    x.achievements.every((a) => isObject(a) && isString(a.id) && isString(a.weekId) && typeof a.granted === "boolean")
  );
}

/** Reads the stored text. `null` (nothing stored yet) gives a fresh save. Never throws. */
export function deserialize(text: string | null): LoadResult {
  if (text === null) return { ok: true, data: emptySave() };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: { kind: "corrupt" } };
  }
  if (!isObject(parsed) || !isNumber(parsed.version) || !("data" in parsed)) return { ok: false, error: { kind: "corrupt" } };

  const migrated = migrateEnvelope({ version: parsed.version, data: parsed.data }, CURRENT_VERSION, MIGRATIONS);
  if ("kind" in migrated) return { ok: false, error: migrated };
  if (!isSaveGame(migrated.data)) return { ok: false, error: { kind: "invalid" } };
  return { ok: true, data: migrated.data };
}
